<template>
  <v-card flat tile class="bulk-budget-creation">
    <res-dialog :showDialog="showHelp"
                titleClass="resBlue darken-1 white--text"
                iconColor="white"
                cancelButtonText="Close"
                :cancelButtonOnly="true"
                cancelButtonColor="resBlue darken-1"
                :maxWidth="800"
                :setWidth="800"
                :setHeight="600"
                @cancel="closeHelp">
      <bulk-budget-creation-help :openSection="openHelpSection"></bulk-budget-creation-help>
    </res-dialog>
    <v-card-text>
      <v-card outlined rounded class="mx-auto pa-3 outline-thick outline-blue">
        <v-row class="bordered-bottom pl-2 py-2">
          <v-col class="d-flex" sm="11">
            <span class="text-adjust">
              I want to create budgets for
            </span>
            <span class="short">
              <res-select id="new-bulk-year-selector" attach="#new-bulk-year-selector" v-model="newBulkSettings.year" :items="years" :loading="currentLoading" :placeholder="'select'"></res-select>
            </span>
            <span class="text-adjust">
              starting in
            </span>
            <span class="medium">
              <res-select id="new-bulk-month-selector" attach="#new-bulk-month-selector" v-model="newBulkSettings.startMonth" :items="months" :loading="currentLoading" :placeholder="'select'"></res-select>
            </span>
            <span class="text-adjust">
              and I want my accounting basis to be
            </span>
            <span class="short">
              <res-select id="new-bulk-accounting-basis-selector" attach="#new-bulk-accounting-basis-selector" v-model="newBulkSettings.accountingBasisId" :items="accountingBasii" :loading="currentLoading" :placeholder="'select'"></res-select>
            </span>
          </v-col>
          <v-col class="d-flex" sm="1">
            <div class="help-controls" @click="openHelp(null)">
              <span class="help-label">Help</span>
              <v-icon color="var(--v-resBlue-darken1)" class="pb-1" small>mdi-help-circle</v-icon>
            </div>
          </v-col>
        </v-row>
        <v-row class="bordered-bottom pl-2 py-2">
          <v-col class="d-flex" sm="12">
            <span class="text-adjust">
              I want my GL Account template to be
            </span>
            <span class="long">
              <all-props-gl-temp-selector ref="glTemplateSelector" v-model="newBulkSettings.templateId"></all-props-gl-temp-selector>
            </span>
            <span class="text-adjust">
              and I want my Settings template to be
            </span>
            <span class="long">
              <all-props-settings-template-selector ref="settingsTemplateSelector" v-model="newBulkSettings.settingsTemplateId"></all-props-settings-template-selector>
            </span>
          </v-col>
        </v-row>
        <v-row class="bordered-bottom pl-2 pt-2 pb-0">
          <v-col class="d-flex" sm="12">
            <span class="text-adjust">
              Based upon these selections, my Projected Vacancies start date will be
            </span>
            <span class="medium">
              <res-text-field v-model="newBulkVacanciesStartDateDisplayable" placeholder="--" :readonly="dateReadonly"></res-text-field>
            </span>
            <span class="text-adjust">
              but I can change my Projected Vacancies on a property by property basis later if desired.
            </span>
            <v-tooltip top max-width="500px" nudge-right="150">
              <span>Start month defaults to the beginning of your accounting year. This can be changed. You can budget for any 12 month period desired.</span>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="inline">
                  <v-icon color="resBlue darken-1">info</v-icon>
                </div>
              </template>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row class="bordered-bottom pl-2 pt-2 pb-0">
          <v-col class="d-flex pb-2" sm="12">
            <span class="text-adjust">
              Apply to
            </span>
            <v-combobox ref="newBulkPropertiesCombo" color="resBlue darken-1" dense outlined class="py-0" chips multiple v-model="newBulkSettings.propsOrGroups" :items="selectablePropertiesAndPropGroups" label="select" single-line>
              <span v-if="selectablePropertiesAndPropGroups.length == 0"
                    class="grey--text text-caption">
                select
              </span>
              <template v-slot:selection="{ item, index, attrs }">
                <v-chip v-bind="attrs" v-if="index < 3"
                        color="resBlue darken-1"
                        class="white--text"
                        close
                        @click:close="removeProp(item)">
                  {{ item.text }}
                </v-chip>
                <span v-if="index === 3"
                      class="grey--text text-caption">
                  (+{{ newBulkSettings.propsOrGroups.length - 3 }} others)
                </span>
              </template>
            </v-combobox>
          </v-col>
        </v-row>
        <v-row class="bordered-bottom pl-2 pt-2 pb-0">
          <v-col class="d-flex pb-2" sm="5">
            <span class="text-adjust d-flex pt-5">
              (Optional) I want to copy working rows from the following budget:
              <v-tooltip top max-width="500px" class="inline">
                <span>Only the Working Rows will be copied. No numbers from the source budget will be copied.</span>
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on" class="">
                    <v-icon class="nudge-left">info</v-icon>
                  </div>
                </template>
              </v-tooltip>
            </span>
          </v-col>

          <v-col class="d-flex pb-2" sm="3">
            <span class="long">
              <label>Property</label>
              <property-selector ref="wrPropertySelector" v-model="copyWorkingRowsPropertyId" elementId="copy-wr-property-selector"></property-selector>
            </span>
          </v-col>

          <v-col class="d-flex pb-2" sm="2">
            <span class="long">
              <label>Year</label>
              <fiscal-year-selector v-model="copyWorkingRowsYear" :propertyId="copyWorkingRowsPropertyId.value" elementId="copy-wr-year-selector" withBudget></fiscal-year-selector>
            </span>
          </v-col>

          <v-col class="d-flex pb-2" sm="2">
            <span class="long">
              <label>Budget</label>
              <res-select class="copy-wr-budget-selector" v-model="cloneWorkingRowsFromBudgetIterationId" :items="copyableWorkingRowSets" :loading="currentLoading"></res-select>
            </span>
          </v-col>
          <v-col></v-col>
        </v-row>
          <v-card-actions class="mt-2 pb-0">
            <v-row class="pl-2 pt-2 pb-0">
              <v-col class="d-flex" sm="10">
                <strong style="color: darkred;" class="text-adjust">
                  This process may take a few minutes. Please do not attempt to manually create budgets while this process is running.
                </strong>
              </v-col>
              <v-col class="d-flex" sm="2">
                <v-btn height="30px" rounded elevation="0" outlined class="resGrey white--text mr-2" @click="clearNewBulk">
                  CLEAR
                </v-btn>
                <v-btn height="30px" :loading='currentLoading' rounded elevation="0" :disabled="applyNewBulkDisabled" class="resGreen white--text" @click="applyNewBulk">
                  APPLY
                </v-btn>
              </v-col>
            </v-row>
          </v-card-actions>
      </v-card>

      <v-card outlined rounded class="mx-auto mt-5">
        <v-tabs v-model="subAreaTab">
          <v-tab>
            PREVIOUSLY APPLIED
          </v-tab>
        </v-tabs>
      </v-card>

      <v-tabs-items v-model="subAreaTab">
        <v-tab-item>
          <v-card outlined rounded class="mx-auto pa-3 mb-2 mt-5 deep-margin-bottom">
            <v-row class="pl-2 py-2">
              <v-col class="d-flex" sm="12">
                <div class="page-controls">
                  <div class="previous-years-controls">
                    <span class="previous-years-label">Show me Bulk Budget Creations for </span>
                    <span class="short">
                      <bulk-creations-year-selector :value="previousAppliedYear" :triggerLoad="triggerYearLoad" @existing-years-loaded="setExistingYears" @input="reloadBulkCreationsForYear" class="inline-block"></bulk-creations-year-selector>
                    </span>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card>

          <div v-show="previousAppliedYear" class="bump-through-deep-margin">
            <template v-for="(prevItem, i) in previousAppliedItems">
              <v-card outlined rounded class="outline-thick mx-auto pa-3 mb-4" :key="prevItem.bulkCreationId">
                <v-row class="bordered-bottom pl-2 py-2">
                  <v-col sm="12">
                    <strong>
                      Note: Below settings were applied by {{prevItem.appliedByName}} on {{localizeDate(prevItem.appliedTimestamp) | moment('MM/DD/YYYY hh:mm:ss A')}}
                    </strong>
                  </v-col>
                </v-row>
                <v-row class="bordered-bottom pl-2 py-2">
                  <v-col class="d-flex" sm="12">
                    <span class="text-adjust">
                      I want to create budgets for
                    </span>
                    <span class="short">
                      <res-select :id="'new-bulk-year-selector'+i" :attach="'#new-bulk-year-selector'+i" v-model="prevItem.year" :items="existingYears" :loading="currentLoading" :placeholder="'select'" :readonly="previousAppliedReadonly"></res-select>
                    </span>
                    <span class="text-adjust">
                      starting in
                    </span>
                    <span class="medium">
                      <res-select :id="'new-bulk-month-selector'+i" :attach="'#new-bulk-month-selector'+i" v-model="prevItem.startMonth" :items="months" :loading="currentLoading" :placeholder="'select'" :readonly="previousAppliedReadonly"></res-select>
                    </span>
                    <span class="text-adjust">
                      and I want my accounting basis to be
                    </span>
                    <span class="short">
                      <res-select :id="'new-bulk-accounting-basis-selector'+i" :attach="'#new-bulk-accounting-basis-selector'+i" v-model="prevItem.accountingBasisId" :items="accountingBasii" :loading="currentLoading" :placeholder="'select'" :readonly="previousAppliedReadonly"></res-select>
                    </span>
                  </v-col>
                </v-row>
                <v-row class="bordered-bottom pl-2 py-2">
                  <v-col class="d-flex" sm="12">
                    <span class="text-adjust">
                      I want my GL Account template to be
                    </span>
                    <span class="long">
                      <all-props-gl-temp-selector :ref="'glTemplateSelector'+i" v-model="prevItem.templateId" :readonly="previousAppliedReadonly"></all-props-gl-temp-selector>
                    </span>
                    <span class="text-adjust">
                      and I want my Settings template to be
                    </span>
                    <span class="long">
                      <all-props-settings-template-selector :ref="'settingsTemplateSelector'+i" v-model="prevItem.settingsTemplateId" :readonly="previousAppliedReadonly"></all-props-settings-template-selector>
                    </span>
                  </v-col>
                </v-row>
                <v-row class="bordered-bottom pl-2 pt-2 pb-0">
                  <v-col class="d-flex" sm="12">
                    <span class="text-adjust">
                      Based upon these selections, my Projected Vacancies start date will be
                    </span>
                    <span class="medium">
                      <res-text-field v-model="prevItem.vacanciesStartDateDisplayable" placeholder="--" :readonly="previousAppliedReadonly"></res-text-field>
                    </span>
                    <span class="text-adjust">
                      but I can change my Projected Vacancies on a property by property basis later if desired.
                    </span>
                  </v-col>
                </v-row>
                <v-row class="bordered-bottom pl-2 pt-2 pb-0">
                  <v-col class="d-flex pb-0" sm="12">
                    <span class="text-adjust">
                      Apply to
                    </span>
                    <v-combobox :ref="'newBulkPropertiesCombo'+i" color="resBlue darken-1" dense outlined class="py-0" chips multiple v-model="prevItem.propsOrGroups" :items="prevItem.propsOrGroups">
                      <template v-slot:selection="{ item, index, attrs }">
                        <v-chip v-bind="attrs" v-if="index < 3">
                          {{ item.text }}
                        </v-chip>
                        <span v-if="index === 3"
                              class="grey--text text-caption">
                          (+{{ prevItem.propsOrGroups.length - 3 }} others)
                        </span>
                      </template>
                    </v-combobox>
                  </v-col>
                </v-row>
                <v-row class="bordered-bottom pl-2 py-2" v-if="prevItem.copyWorkingRowsFromBudget && prevItem.copyWorkingRowsFromBudget.length">
                  <v-col>
                    <span class="text-adjust">
                      (Optional) I want to copy working rows from the following budget: <strong>{{prevItem.copyWorkingRowsFromBudget}}</strong>
                    </span>
                  </v-col>
                </v-row>
                <v-row class="pl-2 py-2" v-if="prevItem.bulkCreateErrors.length > 0">
                  <v-col>
                    <span class="text-adjust">
                      One or more properties did not have budgets created for them due to errors. <a class="underline" @click="openBudgetsNotCreatedDialog(prevItem.bulkCreateErrors)">View these Properties.</a>
                    </span>
                  </v-col>
                </v-row>
              </v-card>
            </template>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card-text>

    <confirm-bulk-creation-dialog v-if="showConfirmBulkCreationDialog"
                                  :showDialog="showConfirmBulkCreationDialog"
                                  :bulkCreateErrors="newBulkSettings.bulkCreateErrors"
                                  :maxWidth="800"
                                  :setWidth="800"
                                  @confirm="confirmBulkCreation"
                                  @cancel="closeConfirmBulkCreationDialog">
    </confirm-bulk-creation-dialog>
    <budgets-not-created-dialog v-if="showBudgetsNotCreatedDialog"
                                :showDialog="showBudgetsNotCreatedDialog"
                                :bulkCreateErrors="budgetsNotCreatedErrors"
                                :maxWidth="800"
                                :setWidth="800"
                                @close="closeBudgetsNotCreatedDialog">
    </budgets-not-created-dialog>
    <v-overlay :value="overlay">
      <v-container>
        <v-row class="fill-height"
               align-content="center"
               justify="center">
          <v-col cols="12" dark>
            <div id="Loading"><div id="LoadingImage" class="shadow ui-corner-all"></div></div>
          </v-col>
        </v-row>
      </v-container>
    </v-overlay>
  </v-card>
</template>

<script>
  import ResSelect from '@/components/base/ResSelect.vue';
  import ResTextField from '@/components/base/ResTextField.vue';
  import ResDialog from '@/components/base/ResDialog.vue';
  import AllPropsGlTempSelector from '@/components/templates/AllPropsGlTempSelector.vue';
  import AllPropsSettingsTemplateSelector from '@/components/templates/AllPropsSettingsTemplateSelector.vue';
  import ConfirmBulkCreationDialog from './dialogs/ConfirmBulkCreationDialog.vue';
  import BudgetsNotCreatedDialog from './dialogs/BudgetsNotCreatedDialog.vue';
  import BulkCreationsYearSelector from './BulkCreationsYearSelector.vue';
  import BulkBudgetCreationHelp from '@/components/help/BulkBudgetCreationHelp.vue';
  import PropertySelector from '@/components/properties/PropertySelector.vue';
  import FiscalYearSelector from '@/components/properties/FiscalYearSelector.vue';

  import Localizable from '@/mixins/Localizable.js';
  import HelpableMixin from '@/mixins/Helpable.js';

  import propertyService from '@/services/property';
  import budgetService from '@/services/budget';

  import * as fiscalYearOperations from '@/utilities/fiscal-year-operations';

  import { mapActions } from 'vuex';
  import { mapGetters } from 'vuex';
  
  export default {
    name: 'BulkBudgetCreation',
    mixins: [Localizable, HelpableMixin],
    components: {
      ResSelect,
      ResTextField,
      ResDialog,
      AllPropsGlTempSelector,
      AllPropsSettingsTemplateSelector,
      ConfirmBulkCreationDialog,
      BudgetsNotCreatedDialog,
      BulkCreationsYearSelector,
      BulkBudgetCreationHelp,
      PropertySelector,
      FiscalYearSelector
    },   
    data: function () {
      return {
        overlay: false,
        showConfirmBulkCreationDialog: false,
        showBudgetsNotCreatedDialog: false,
        years: [],
        yearsFuture: 3,
        existingYears: [],
        months: [
          { text: 'January ', value: 1 },
          { text: 'February ', value: 2 },
          { text: 'March ', value: 3 },
          { text: 'April ', value: 4 },
          { text: 'May ', value: 5 },
          { text: 'June ', value: 6 },
          { text: 'July ', value: 7 },
          { text: 'August ', value: 8 },
          { text: 'September ', value: 9 },
          { text: 'October ', value: 10 },
          { text: 'November ', value: 11 },
          { text: 'December ', value: 12 }
        ],
        accountingBasii: [
          { text: 'Accrual', value: 1 },
          { text: 'Cash', value: 2 }
        ],
        settingsTemplates: [],
        selectablePropertiesAndPropGroups: [],
        dateReadonly: true,
        previousAppliedReadonly: true,
        newBulkSettings: {
          year: null,
          startMonth: null,
          accountingBasisId: null,
          templateId: null,
          settingsTemplateId: null,
          propsOrGroups: [],
          bulkCreateErrors: []
        },
        previousAppliedYear: null,
        previousAppliedItems: [],
        budgetsNotCreatedErrors: [],
        triggerYearLoad: 0,
        subAreaTab: 0,
        copyWorkingRowsYear: 0,
        copyWorkingRowsPropertyId: {},
        copyableWorkingRowSets: [],
        cloneWorkingRowsFromBudgetIterationId: null
      };
    },
    props: {
     
      
    },
    methods: {
      ...mapActions(['changeLoading']),
      async loadInitialData() {
        this.changeLoading = true;

        this.years = [];
        var currentYear = (new Date()).getFullYear();
        for (var i = 0; i < this.yearsFuture; i++) {
          var year = currentYear + i;         
          this.years.push(year);
        }

        let propertyData = await propertyService.getDropdown(true);
        if (propertyData && propertyData.data) {
          this.selectablePropertiesAndPropGroups = propertyData.data;
        } else {
          this.selectablePropertiesAndPropGroups = [];
        }

        await this.loadPreviousAppliedItems();

        this.changeLoading = false;
      },

      async loadPreviousAppliedItems() {
        if (this.previousAppliedYear == null) {
          this.previousAppliedItems = [];
            return;
          }
        this.loading = true;

        let bulkData = await budgetService.getAllBulkCreations(this.previousAppliedYear);
        if (bulkData && bulkData.data) {
          this.previousAppliedItems = bulkData.data;
        } else {
          this.previousAppliedItems = [];
        }
        this.loading = false;
      },
      removeProp(item) {
        const index = this.newBulkSettings.propsOrGroups.indexOf(item)
        if (index >= 0) this.newBulkSettings.propsOrGroups.splice(index, 1)
      },
      clearNewBulk() {
        this.previousAppliedYear = null;
        this.newBulkSettings = {
          year: null,
          startMonth: null,
          accountingBasisId: null,
          templateId: null,
          settingsTemplateId: null,
          propsOrGroups: [],
          bulkCreateErrors: []
        };
      },
      async applyNewBulk() {
        this.changeLoading = true;
       
        // send api call for checking all props and groups selected
        var errorResponse = await budgetService.checkForBulkBudgetCreation(this.newBulkSettings);

        if (!errorResponse || !errorResponse.data || errorResponse.data.length > 0) {
          this.newBulkSettings.bulkCreateErrors = (errorResponse && errorResponse.data) ? errorResponse.data.sort((a, b) => {
            if (a.propertyAbbreviation < b.propertyAbbreviation) { return -1; }
            if (a.propertyAbbreviation > b.propertyAbbreviation) { return 1; }
            return 0;
          }) : [{ propertyAbbreviation: '---', error: 'Unable to retrieve data from ResMan Core. Please try again or contact ResMan Support.' }];
          this.showConfirmBulkCreationDialog = true;

          this.changeLoading = false;
        } else {
          await this.confirmBulkCreation();
          this.changeLoading = false;
        }
      },
      closeConfirmBulkCreationDialog() {
        this.clearNewBulk();
        this.showConfirmBulkCreationDialog = false;
      },
      async confirmBulkCreation() {
        this.showConfirmBulkCreationDialog = false;
        this.overlay = true;

        //clone working rows if applicable
        if (this.copyWorkingRows) {
          this.newBulkSettings.cloneWorkingRowsFromBudgetIterationId = this.cloneWorkingRowsFromBudgetIterationId;
        }

        //start creating those budgets, minus the error props
        var response = await budgetService.createBulkBudgets(this.newBulkSettings);

        if (response) {
          // refresh prev items display

          this.triggerYearLoad++;

          console.log("bulk creation succeeded!");
          this.clearNewBulk();
          this.overlay = false;
        } else {
          console.log("bulk creation failed");
          this.overlay = false;
        }
      },
      async reloadBulkCreationsForYear(year) {
        this.previousAppliedYear = year;
        await this.loadPreviousAppliedItems();
      },
      setExistingYears(years) {
        this.existingYears = years;
      },
      openBudgetsNotCreatedDialog(bulkCreateErrors) {
        bulkCreateErrors.sort((a, b) => {
          if (a.propertyAbbreviation < b.propertyAbbreviation) { return -1; }
          if (a.propertyAbbreviation > b.propertyAbbreviation) { return 1; }
          return 0;
        });
        this.budgetsNotCreatedErrors = bulkCreateErrors;
        this.showBudgetsNotCreatedDialog = true;
      },
      closeBudgetsNotCreatedDialog() {
        this.showBudgetsNotCreatedDialog = false;
        this.budgetsNotCreatedErrors = [];
      },
      resetWRCopy() {
        this.copyWorkingRowsYear = null;
        this.copyableWorkingRowSets = [];
        this.cloneWorkingRowsFromBudgetIterationId = null
      }
    },
    computed: {
      ...mapGetters(['currentLoading']),
      excludedProps() {
        return this.bulkCreateErrors.map(item => item.propertyId);
      },
      newBulkVacanciesStartDateDisplayable() {
        if (this.newBulkVacanciesStartDate != null) {
          var options = { year: 'numeric', month: 'long', day: 'numeric' };
          var date = new Date(this.newBulkVacanciesStartDate);
          var yearMinusOne = date.getMonth()==11? date.getFullYear() :date.getFullYear()-1;
          date.setFullYear(yearMinusOne);
          if (date.getMonth() == 2 && date.getDate() == 1) {
            date.setMonth(1);
            date.setDate(28);
          }
          else if (date.getMonth() == 1 && date.getDate() == 28 && (yearMinusOne % 4 === 0 && yearMinusOne % 100 !== 0) || (yearMinusOne % 400 === 0)) {

            date.setMonth(1);
            date.setDate(29);
          }
          return date.toLocaleDateString("en-US", options);
        } else {
          return this.newBulkVacanciesStartDate;
        }
      },
      newBulkVacanciesStartDate() {
        if (this.newBulkSettings.year != null && this.newBulkSettings.startMonth != null) { 
          return fiscalYearOperations.getEndOfPriorFiscalYear(this.newBulkSettings.startMonth, this.newBulkSettings.year);
        } else {
          return null;
        }
      },
      applyNewBulkDisabled() {
        if (this.newBulkSettings.year != null && this.newBulkSettings.startMonth != null && this.newBulkSettings.accountingBasisId != null && this.newBulkSettings.templateId != null && this.newBulkSettings.settingsTemplateId != null && this.newBulkSettings.propsOrGroups.length > 0) {
          return false;
        } else {
          return true;
        }
      },
      copyWorkingRows() {
        return this.copyWorkingRowsPropertyId && this.copyWorkingRowsPropertyId.value && this.copyWorkingRowsYear && this.cloneWorkingRowsFromBudgetIterationId;
      }
    },
    async created() {
      this.$eventHub.$on('replicatedEntitiesSynced', await this.loadInitialData);
    },
    async mounted() {
      await this.loadInitialData();
    },
    watch: {
      'newBulkSettings.propsOrGroups': function (newVal) {
        if (newVal && newVal.length > 0) {
          var newNewVal = newVal.filter((nV) => typeof nV == 'object');
          if (newNewVal.length != newVal.length) {
            this.newBulkSettings.propsOrGroups = newNewVal;
          }
        }
      },
      'copyWorkingRowsYear': async function (newVal) {
        if (newVal) {
          this.changeLoading = true;
          var budgetList = await budgetService.getAll(this.copyWorkingRowsPropertyId.value, false, this.copyWorkingRowsYear, 0);
          this.copyableWorkingRowSets = (budgetList.data && budgetList.data.map((b) => { return { text: b.accountingBasis + ' | ' + b.version, value: b.budgetIterationId } })) || [];
          this.changeLoading = false;
        }
      },
      'copyWorkingRowsPropertyId': function () {
        this.resetWRCopy();
      }
    }
  };
</script>

<style lang="scss" scoped>
  #Loading {
    z-index: 1010;
    position: absolute;
    top: 50%;
    left: 50%;
    margin-left: -120px;
    border: 1px solid #D7D7D7;
  }

  .bordered-bottom {
      border-bottom: 1px solid var(--v-resGrey-lighten1);
  }
  strong {
      color: var(--v-resGrey-darken1);
  }
  .text-adjust {
      padding-top:5px;
      padding-left:5px;
      padding-right:5px;
  }
  .underline {
      text-decoration:underline;
  }
  .deep-margin-bottom {
      margin-bottom: 200px !important;
  }
  .bump-through-deep-margin {
      margin-top:-180px;
  }
  #LoadingImage {
    background: #fff url(../../assets/ajax-loader.gif) no-repeat center;
    width: 240px;
    height: 40px;
    margin: auto;
  }
  .outline-thick {
    border: 3px solid var(--v-resGrey-lighten1);
    border-radius: 10px !important;
  }
  .outline-blue {
      border-color: var(--v-resBlue-darken1) !important;
  }
  span.short {
      max-width: 150px;
  }
  span.medium {
      max-width: 225px;
  }
  span.long {
      max-width: 450px;
  }
  .inline-block {
      display:inline-block;
  }
</style>

<style>

  .bulk-budget-creation .v-text-field__details {
    display: none; /*remove empty clickable area that prevents closing combobox*/
  }
</style>
